import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { QuestionFormComponent, QuestionSubmission } from '../components/question-form/question-form.component';
import { ChatService } from '@app/core/services/chat.service';
import { RagApiService, RagResponse, ApiMode } from '@app/core/services/rag-api.service';
import { LogoutComponent } from '@app/features/logout/logout.component';
import { VodafoneIconComponent } from '@app/shared/icons/vodafone.component';
import { FeedbackService, FeedbackData, Application, IssueResponse, ChatHistory } from '@app/core/services/feedback.service';
import { UserService } from '@app/core/services/user.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';
import { questionAnimations } from './question.animations';

// Interface for handling messages
interface Message {
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  htmlContent?: SafeHtml; // For parsed markdown
}

// Declare the marked library which will be loaded via CDN
declare const marked: any;

@Component({
  selector: 'app-question',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    QuestionFormComponent,
    LogoutComponent,
    VodafoneIconComponent
  ],
  templateUrl: './question.component.html',
  styleUrls: ['./question.component.css'],
  animations: [
    questionAnimations.chatExpand,
    questionAnimations.fadeIn,
    questionAnimations.slideFade
  ]
})
export class QuestionComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;
  private shouldScrollToBottom = false;

  isSubmitting = false;
  error: string | null = null;
  private sessionStartTime = Date.now();
  private currentQuestion = '';
  currentAnswer = '';
  hasConversation = false;
  messages: Message[] = [];
  feedbackGiven = false;
  inputContainerHeight = 150; // Default height in pixels (increased to accommodate taller textarea)
  userName: string = ''; // Property to store user's full name

  // Sidebar state
  isTicketSidebarVisible = false;
  ticketForm: FormGroup;

  applications: Application[] = [];
  filteredApplications: Application[] = [];
  selectedApplications: Application[] = [];
  private applicationSearchSubject = new Subject<string>();

  // Add these properties to the class
  private readonly PAGE_SIZE = 20;
  private currentPage = 0;
  isLoadingMoreApps = false;
  hasMoreApps = true;

  // All applications from the API
  private allApplications: Application[] = [];

  private userSubscription!: Subscription;

  constructor(
    private router: Router,
    private chatService: ChatService,
    private ragApiService: RagApiService,
    private feedbackService: FeedbackService,
    private userService: UserService,
    private sanitizer: DomSanitizer,
    private fb: FormBuilder
  ) {
    // Clear any existing state when arriving on the question page
    this.chatService.clearChat();
    this.ragApiService.clearStoredIds();

    // Initialize the ticket form with all required fields
    this.ticketForm = this.fb.group({
      subject: ['', Validators.required],
      description: ['', Validators.required],
      applications: [[], [Validators.minLength(0)]], // At least one app required
      // applications: [[], [Validators.required, Validators.minLength(1)]], // At least one app required
      impactLevel: ['', Validators.required],
      urgency: ['', Validators.required],
      priority: [this.feedbackService.FIXED_PRIORITY] // Fixed to P4, no validation needed
      // History, satisfied, and duration are handled programmatically
    });
  }

  ngOnInit(): void {
    // Dynamically load the marked library from CDN
    if (typeof marked === 'undefined') {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/marked/marked.min.js';
      script.async = true;
      document.head.appendChild(script);
    }

    // Load the user's name from the user service
    // Subscribe to user$ to get user data (automatically loaded from sessionStorage)
    this.userSubscription = this.userService.user$.subscribe(user => {
      if (user) {
        this.userName = user.full_name;
      }
    });

    // Initial load of applications (first page only)
    this.loadApplications();
  }

  ngOnDestroy(): void {
    this.userSubscription.unsubscribe();
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  private scrollToBottom(): void {
    if (this.messagesContainer) {
      const container = this.messagesContainer.nativeElement;
      container.scrollTop = container.scrollHeight;
    }
  }

  // Convert markdown to HTML safely
  parseMarkdown(markdown: string): SafeHtml {
    // Check if marked is available
    if (typeof marked !== 'undefined') {
      // Parse markdown to HTML and sanitize with Angular's sanitizer
      return this.sanitizer.bypassSecurityTrustHtml(marked.parse(markdown));
    }

    // Fallback to plain text if marked is not loaded yet
    return this.sanitizer.bypassSecurityTrustHtml(
      `<p>${markdown.replace(/\n/g, '<br>')}</p>`
    );
  }

  onQuestionSubmit(submission: QuestionSubmission): void {
    if (this.isSubmitting) return;

    this.isSubmitting = true;
    this.error = null;

    // Store the question for feedback
    this.currentQuestion = submission.question;

    // Set hasConversation to true when a question is submitted
    this.hasConversation = true;

    // Add user message to the chat
    this.messages.push({
      type: 'user',
      content: submission.question,
      timestamp: new Date()
    });
    this.shouldScrollToBottom = true;

    // Prepare the request
    const request = {
      actor: 'user',
      question: submission.question,
      history: this.buildHistoryForBackend()
    };

    // Get conversation ID from storage
    const conversationId = this.ragApiService.getStoredConversationId();

    // Call the appropriate endpoint based on whether we have a conversation ID (ENDUSER mode)
    const apiCall = conversationId
      ? this.ragApiService.continue(conversationId, { ...request, load_history: true }, ApiMode.ENDUSER)
      : this.ragApiService.predict(request, ApiMode.ENDUSER);

    // First, get the conversation and prompt IDs
    apiCall.subscribe({
      next: (response) => {
        // Now poll for the actual response
        this.ragApiService.pollResponse(response.promptID, ApiMode.ENDUSER).subscribe({
          next: (pollResponse) => {
            this.currentAnswer = pollResponse.output;

            // Add assistant message to the chat with markdown parsing
            const assistantMessage: Message = {
              type: 'assistant',
              content: this.currentAnswer,
              timestamp: new Date(),
              htmlContent: this.parseMarkdown(this.currentAnswer)
            };

            this.messages.push(assistantMessage);
            this.shouldScrollToBottom = true;
            this.isSubmitting = false;
          },
          error: (error) => {
            console.error('Error polling response:', error);
            this.error = 'Failed to get answer. Please try again.';
            this.isSubmitting = false;
          }
        });
      },
      error: (error) => {
        console.error('Error getting conversation:', error);
        this.error = 'Failed to start conversation. Please try again.';
        this.isSubmitting = false;
      }
    });
  }

  loadApplications(query?: string, reset = false): void {
    if (reset) {
      this.currentPage = 0;
      this.filteredApplications = [];
      this.hasMoreApps = true;
      this.searchQuery = query || '';
    }

    if (!this.hasMoreApps || this.isLoadingMoreApps) {
      return;
    }

    this.isLoadingMoreApps = true;

    // If we already have applications loaded, filter and paginate client-side
    if (this.allApplications.length > 0 && !reset) {
      this.paginateApplications();
      return;
    }

    // Otherwise fetch from API
    this.feedbackService.getApplications(query).subscribe({
      next: (response) => {
        // Store all applications for client-side filtering and pagination
        this.allApplications = response.applications; // MOCKME

        // Apply initial pagination
        this.paginateApplications();
      },
      error: (error) => {
        console.error('Error fetching applications:', error);
        this.isLoadingMoreApps = false;
      }
    });
  }

  buildHistoryForBackend(): ChatHistory[] {
    const temphist = this.messages
      .filter(m => m.type === 'user' || m.type === 'assistant')
      .map((m, i, arr) => ({
        question: m.type === 'user' ? m.content : (i > 0 ? arr[i - 1].content : ''),
        answer: m.type === 'assistant' ? m.content : '',
        timestamp: m.timestamp.toISOString(),
        type: m.type
      }))
      .filter(m => m.type === 'assistant')
      .map((x, i, arr) => ({
        question: x.question,
        answer: x.answer,
        timestamp: x.timestamp
      }))
    return temphist
  }

  submitTicket(): void {
    if (this.ticketForm.invalid) return;

    // Get form values
    const formValues = this.ticketForm.value;

    // Verify that at least one application has been provided
    if (!formValues.applications || formValues.applications.length === 0) {
      alert('Select at least one application from the list.');
      return;
    }

    // Create the feedback data with form values
    const feedbackData: FeedbackData = {
      history: this.buildHistoryForBackend(),
      satisfied: false,
      duration: Math.floor((Date.now() - this.sessionStartTime) / 1000), // seconds
      subject: formValues.subject,
      description: formValues.description,
      priority: this.feedbackService.FIXED_PRIORITY, // P4 forced
      impactLevel: formValues.impactLevel,
      urgency: formValues.urgency,
      applications: formValues.applications
    };

    this.feedbackGiven = true;

    // Submit using the feedback service
    this.feedbackService.createServiceRequest(feedbackData).subscribe({
      next: (response: IssueResponse) => {
        console.log('Ticket created successfully', response);
        this.hideTicketSidebar();

        // Show a success message using the key property from the new API response
        const ticketNumber = response?.key || 'VF-' + Math.floor(Math.random() * 10000);
        alert(`Service Request Ticket ${ticketNumber} created successfully!`);
      },
      error: (error: Error) => {
        console.error('Error creating ticket', error);
        // Show an error message
        alert('Error creating service request ticket. Please try again.');
        this.feedbackGiven = false;
      }
    });
  }

  submitFeedback(satisfied: boolean): void {
    // Set feedback status to true
    this.feedbackGiven = true;

    // Submit feedback data
    const feedbackData: FeedbackData = {
      history: this.buildHistoryForBackend(),
      satisfied: satisfied,
      duration: Math.floor((Date.now() - this.sessionStartTime) / 1000), // seconds
      subject: this.currentQuestion.substring(0, 50) + (this.currentQuestion.length > 50 ? '...' : ''),
      description: this.currentQuestion,
      priority: this.feedbackService.FIXED_PRIORITY
    };

    this.feedbackService.submitFeedback(feedbackData).subscribe({
      next: (response: IssueResponse) => {
        console.log('Ticket created successfully', response);
        this.hideTicketSidebar();

        // Show a success message using the key property from the new API response
        const ticketNumber = response?.key || 'VF-' + Math.floor(Math.random() * 10000);
        alert(`Conversation ticket ${ticketNumber} created successfully!`);
      },
      error: (error: Error) => {
        console.error('Error submitting feedback', error);
        // Show an error message
        alert('Error submitting feedback. Please try again.');
        this.feedbackGiven = false
      }
    });
  }

  markHelpful(): void {
    this.submitFeedback(true);
  }

  markNotHelpful(): void {
    this.submitFeedback(false);
  }

  // Show the ticket sidebar and prefill form data
  showTicketSidebar(): void {
    // Only set default values
    this.ticketForm.patchValue({
      subject: '',
      description: '',
      applications: [],
      impactLevel: 'medium',
      urgency: 'medium',
      priority: this.feedbackService.FIXED_PRIORITY // Imposta sempre il P4 dal servizio
    });

    // Forza il campo priority a essere disabilitato
    this.ticketForm.get('priority')?.disable();

    this.isTicketSidebarVisible = true;
  }

  // Hide the ticket sidebar
  hideTicketSidebar(): void {
    this.isTicketSidebarVisible = false;
  }

  toggleApplication(app: Application): void {
    const currentApps = [...this.ticketForm.get('applications')?.value || []];
    const index = currentApps.indexOf(app.key);

    if (index === -1) {
      // Add app if not selected
      currentApps.push(app.key);
      this.selectedApplications.push(app);
    } else {
      // Remove app if already selected
      currentApps.splice(index, 1);
      this.selectedApplications = this.selectedApplications.filter(a => a.key !== app.key);
    }

    this.ticketForm.get('applications')?.setValue(currentApps);
    this.ticketForm.get('applications')?.markAsTouched();
  }

  removeApplication(app: Application): void {
    const currentApps = [...this.ticketForm.get('applications')?.value || []];
    const index = currentApps.indexOf(app.key);

    if (index !== -1) {
      currentApps.splice(index, 1);
      this.selectedApplications = this.selectedApplications.filter(a => a.key !== app.key);
      this.ticketForm.get('applications')?.setValue(currentApps);
      this.ticketForm.get('applications')?.markAsTouched();
    }
  }

  isApplicationSelected(app: Application): boolean {
    const currentApps = this.ticketForm.get('applications')?.value || [];
    return currentApps.includes(app.key);
  }

  // Update the onApplicationSearch method to handle client-side filtering
  onApplicationSearch(event: Event): void {
    const input = event.target as HTMLInputElement;
    const query = input.value;

    // Debounce the search
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      // Reset pagination and apply search filter
      this.currentPage = 0;
      this.filteredApplications = [];
      this.searchQuery = query;

      // If we have applications loaded, filter client-side
      if (this.allApplications.length > 0) {
        this.paginateApplications();
      } else {
        // Otherwise load from API with filter
        this.loadApplications(query, true);
      }
    }, 300);
  }

  // Add this property
  private searchTimer: any;

  // Update the scrolling method to work with client-side pagination
  onApplicationsScroll(event: Event): void {
    const element = event.target as HTMLElement;
    const scrollPosition = element.scrollTop + element.clientHeight;
    const scrollHeight = element.scrollHeight;

    // If we're close to the bottom and there are more apps, load more
    if (scrollHeight - scrollPosition < 50 && this.hasMoreApps && !this.isLoadingMoreApps) {
      this.paginateApplications();
    }
  }

  // Add this property
  searchQuery = '';

  // Client-side pagination
  private paginateApplications(): void {
    // Apply search filter if needed
    let filtered = this.allApplications;
    if (this.searchQuery) {
      const searchLower = this.searchQuery.toLowerCase();
      filtered = this.allApplications.filter(app =>
        app.label.toLowerCase().includes(searchLower) ||
        app.key.toLowerCase().includes(searchLower)
      );
    }

    // Apply pagination
    const startIndex = this.currentPage * this.PAGE_SIZE;
    const endIndex = startIndex + this.PAGE_SIZE;
    const pagedApps = filtered.slice(startIndex, endIndex);

    // Update the filtered applications
    this.filteredApplications = [...this.filteredApplications, ...pagedApps];

    // Check if we've reached the end
    this.hasMoreApps = endIndex < filtered.length;
    this.currentPage++;
    this.isLoadingMoreApps = false;
  }

  // Handle height changes from the input component
  onInputHeightChange(height: number): void {
    // Add a small margin to avoid unwanted scrollbars
    this.inputContainerHeight = Math.max(height + 20, 150); // Ensure the container is at least 150px high
  }
}
